import React, { useState } from 'react';
import { Sparkles, Globe, Loader2, ExternalLink, Shield, MessageCircle, AlertTriangle } from 'lucide-react';
import { apiPost } from '../lib/api.js';
import toast from 'react-hot-toast';

/**
 * AIIntelTab — Perplexity Sonar-powered intel inside the LeadDrawer.
 * Shows web-grounded enrichment + live objection handler for the active lead.
 */
export default function AIIntelTab({ lead }) {
  const [enrichment, setEnrichment] = useState(null);
  const [enrichBusy, setEnrichBusy] = useState(false);

  const [objection, setObjection] = useState('');
  const [rebuttal, setRebuttal] = useState(null);
  const [objBusy, setObjBusy] = useState(false);

  async function runEnrichment() {
    setEnrichBusy(true);
    try {
      const r = await apiPost('/ai/lead-enrichment', { leadId: lead._id });
      setEnrichment(r);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enrichment failed');
    } finally {
      setEnrichBusy(false);
    }
  }

  async function runObjection() {
    if (!objection.trim()) return;
    setObjBusy(true);
    try {
      const r = await apiPost('/ai/objection-handler', {
        objection,
        product: lead.vertical || 'life insurance',
        state: lead.state,
        leadName: lead.firstName,
      });
      setRebuttal(r);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Objection handler failed');
    } finally {
      setObjBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header pill */}
      <div className="flex items-center gap-2 text-xs text-white/60">
        <Globe className="w-3.5 h-3.5 text-civix-cyan" />
        <span>Powered by Perplexity Sonar — web-grounded, sourced answers</span>
      </div>

      {/* Lead enrichment */}
      <div className="panel-light p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-sm font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-civix-purple" /> Lead Intel
          </div>
          <button
            onClick={runEnrichment}
            disabled={enrichBusy}
            className="btn-primary text-xs flex items-center gap-1.5"
          >
            {enrichBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
            {enrichBusy ? 'Researching…' : enrichment ? 'Refresh' : 'Run Web Research'}
          </button>
        </div>

        {!enrichment && !enrichBusy && (
          <div className="text-xs text-white/50">
            Pulls public intel before you dial — recent life events, professional context, opener angles.
          </div>
        )}

        {enrichment && (
          <div className="space-y-3 text-sm">
            <div>
              <div className="label mb-1">Summary</div>
              <div className="text-white/90">{enrichment.summary}</div>
            </div>

            {enrichment.lifeEvents?.length > 0 && (
              <div>
                <div className="label mb-1">Recent Life Events</div>
                <ul className="text-white/80 text-xs space-y-1">
                  {enrichment.lifeEvents.map((e, i) => (
                    <li key={i} className="flex gap-2"><span className="text-civix-cyan">•</span>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {enrichment.professionalContext && (
              <div>
                <div className="label mb-1">Professional Context</div>
                <div className="text-white/80 text-xs">{enrichment.professionalContext}</div>
              </div>
            )}

            {enrichment.talkingPoints?.length > 0 && (
              <div>
                <div className="label mb-1">Opener Angles</div>
                <ul className="space-y-1">
                  {enrichment.talkingPoints.map((p, i) => (
                    <li key={i} className="text-xs text-white/85 flex gap-2">
                      <span className="text-civix-purple">{i + 1}.</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {enrichment.redFlags?.length > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-red-300 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Caution
                </div>
                <ul className="text-xs text-red-200/90 space-y-0.5">
                  {enrichment.redFlags.map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between text-[10px] text-white/40">
              <span>Confidence: <span className="capitalize text-white/60">{enrichment.confidence}</span></span>
              {enrichment.fallback && <span className="text-amber-300/70">demo / no API key</span>}
            </div>

            <Citations citations={enrichment.citations} />
          </div>
        )}
      </div>

      {/* Objection handler */}
      <div className="panel-light p-4">
        <div className="font-display text-sm font-semibold flex items-center gap-2 mb-3">
          <MessageCircle className="w-4 h-4 text-civix-cyan" /> Live Objection Handler
        </div>
        <div className="text-xs text-white/50 mb-2">
          Type what they just said. Get a sourced rebuttal in 2 seconds.
        </div>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder='e.g. "It\u2019s too expensive" or "I already have insurance"'
            value={objection}
            onChange={(e) => setObjection(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runObjection()}
          />
          <button onClick={runObjection} disabled={objBusy || !objection.trim()} className="btn-primary text-xs">
            {objBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Get Rebuttal'}
          </button>
        </div>

        {rebuttal && (
          <div className="mt-3 space-y-2 text-sm">
            <div className="rounded-lg bg-civix-purple/10 border border-civix-purple/30 p-3">
              <div className="label mb-1">Full Script</div>
              <div className="text-white/95 leading-relaxed">{rebuttal.fullScript}</div>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-white/60 hover:text-white">Breakdown</summary>
              <div className="mt-2 space-y-1.5 text-white/80">
                <div><span className="text-civix-purple font-semibold">Acknowledge:</span> {rebuttal.acknowledge}</div>
                <div><span className="text-civix-purple font-semibold">Reframe:</span> {rebuttal.reframe}</div>
                <div><span className="text-civix-purple font-semibold">Proof point:</span> {rebuttal.proofPoint}</div>
                <div><span className="text-civix-purple font-semibold">Soft close:</span> {rebuttal.softClose}</div>
              </div>
            </details>
            <Citations citations={rebuttal.citations} />
          </div>
        )}
      </div>

      <div className="text-[10px] text-white/30 flex items-center gap-1.5">
        <Shield className="w-3 h-3" />
        AI is research-grade, not legal/medical advice. Always verify carrier guidance with the underwriting desk.
      </div>
    </div>
  );
}

function Citations({ citations }) {
  if (!citations?.length) return null;
  return (
    <div className="pt-2 border-t border-civix-border/50">
      <div className="text-[10px] text-white/40 mb-1.5">Sources</div>
      <div className="flex flex-wrap gap-1.5">
        {citations.slice(0, 6).map((c, i) => (
          <a
            key={i}
            href={c}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border border-civix-cyan/30 text-civix-cyan hover:bg-civix-cyan/10"
          >
            {hostnameOf(c)} <ExternalLink className="w-2.5 h-2.5" />
          </a>
        ))}
      </div>
    </div>
  );
}

function hostnameOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return 'source'; }
}
